import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { AddOrEditMatterScheduledMessageModal } from "@/components/features/settings/defaults/matter-scheduled-messages/AddOrEditMatterScheduledMessageModal";
import { MatterScheduledMessageRowContextMenu } from "@/components/features/settings/defaults/matter-scheduled-messages/MatterScheduledMessageRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { ActionConfirmationModal, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterScheduledMessage } from "@/lib/data/react-query/matters/matter-scheduled-message";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterScheduledMessage } from "@/lib/types";
import {
  MultiValueCell,
  customDateTimeSortingFns,
} from "@/lib/utils/tableUtils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

type MatterScheduledMessageRow = MatterScheduledMessage & {
  incidentType: string[];
};

const MATTER_SCHEDULED_MESSAGE_TABLE_COLUMN_IDS = [
  "Label",
  "Content",
  "Description",
  "Incident Type",
  "Active",
] as const;

const MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY: string =
  tanstackTableNames.matterScheduledMessages;

function MatterScheduledMessagesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterScheduledMessage,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterScheduledMessage,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [activeMatterScheduledMessageId, setActiveMatterScheduledMessageId] =
    useState<string | null>(null);
  const [matterScheduledMessageToDelete, setMatterScheduledMessageToDelete] =
    useState<MatterScheduledMessage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const deleteMatterScheduledMessage = useDeleteMatterScheduledMessage();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(
      MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY,
    );
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(
      MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY,
    );
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(
      MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY,
    );
    const ids = [...MATTER_SCHEDULED_MESSAGE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(
        MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY,
      );
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: matterSettingsData,
    isLoading,
    isError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData?.data.incidentTypes],
  );

  const matterScheduledMessages = useMemo(
    () => matterSettingsData?.data.scheduledMessages ?? [],
    [matterSettingsData?.data.scheduledMessages],
  );

  const rows = useMemo<MatterScheduledMessageRow[]>(
    () =>
      matterScheduledMessages.map((message) => ({
        ...message,
        incidentType: incidentTypes
          .filter((type) => message.incidentTypeIds?.includes(type.id))
          .map((type) => getIncidentTypeLabel(type)),
      })),
    [matterScheduledMessages, incidentTypes],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const label = row.label.toLowerCase();
      const content = row.content.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      const incidentTypeLabels = row.incidentType.join(" ").toLowerCase();
      const active = row.active ? "active" : "inactive";
      return (
        label.includes(q) ||
        content.includes(q) ||
        description.includes(q) ||
        incidentTypeLabels.includes(q) ||
        active.includes(q)
      );
    });
  }, [rows, searchQuery]);

  const columns = useMemo<ColumnDef<MatterScheduledMessageRow>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => row.original.label,
        sortingFn: "alphanumeric",
      },
      {
        id: "Content",
        accessorFn: (row) => row.content,
        header: "Content",
        cell: ({ row }) => row.original.content || "-",
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => row.original.description ?? "-",
        sortingFn: "alphanumeric",
      },
      {
        id: "Incident Type",
        accessorFn: (row) => row.incidentType.join(", "),
        header: "Incident type",
        cell: ({ row }) => <MultiValueCell value={row.original.incidentType} />,
        sortingFn: "alphanumeric",
      },
      {
        id: "Active",
        accessorFn: (row) => row.active,
        header: "Active",
        cell: ({ row }) => (row.original.active ? "Active" : "Inactive"),
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_SCHEDULED_MESSAGES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const activeMatterScheduledMessage = matterScheduledMessages.find(
    (message) => message.id === activeMatterScheduledMessageId,
  );

  const handleDeleteMatterScheduledMessage = async () => {
    if (!matterScheduledMessageToDelete) return;

    await deleteMatterScheduledMessage.mutateAsync({
      id: matterScheduledMessageToDelete.id,
    });
    setMatterScheduledMessageToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={MATTER_SCHEDULED_MESSAGE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search messages...",
        }}
        isLoading={isLoading}
        isError={isError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No scheduled messages found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="messages"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterScheduledMessages,
          "data-table-name": tanstackTableNames.matterScheduledMessages,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            const id = row.original.id;
            if (!id) return;
            setActiveMatterScheduledMessageId(id);
            setIsEditing(true);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterScheduledMessageRowContextMenu
            message={row.original}
            trigger={trigger}
            canEdit={hasEditPermission}
            canDelete={hasDeletePermission}
            onEdit={() => {
              setActiveMatterScheduledMessageId(row.original.id);
              setIsEditing(true);
            }}
            onDelete={() => {
              setMatterScheduledMessageToDelete(row.original);
            }}
          />
        )}
      />

      {activeMatterScheduledMessageId && activeMatterScheduledMessage ? (
        <AddOrEditMatterScheduledMessageModal
          editing
          open={isEditing}
          setOpen={setIsEditing}
          activeMatterScheduledMessage={activeMatterScheduledMessage}
          isReadOnly={!hasEditPermission || !hasDeletePermission}
        />
      ) : null}

      <AddOrEditMatterScheduledMessageModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
      />

      <ActionConfirmationModal
        open={Boolean(matterScheduledMessageToDelete)}
        setOpen={(open) => {
          if (!open) setMatterScheduledMessageToDelete(null);
        }}
        title="Delete scheduled message"
        description={`Are you sure you want to delete "${matterScheduledMessageToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteMatterScheduledMessage}
      />
    </div>
  );
}

export function MatterScheduledMessagesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterScheduledMessagesTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
